
import React, { useState, useCallback } from 'react';
import { makeStyles } from '~/lib/bn-material-ui';
import InventoryMap from './inventory-map';
import InventoryMapTooltip from './inventory-map-tooltip';

const useStyles = makeStyles(() => ({
  container: {
    position: 'relative',
    width: '100%',
    height: '100%',
  },
}));

/**
 * Hook to manage hover state and tooltip
 * @param {boolean} isInteracting - Whether the user is currently interacting with the map
 * @returns {Object} Hover state and handlers
 */
function useHoverState(isInteracting) {
  const [hoveredBin, setHoveredBin] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });

  const handleMouseEnter = useCallback((bin, event) => {
    if (isInteracting) {
      return;
    }

    const element = event.currentTarget;
    const rect = element.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0 || rect.x === 0 || rect.y === 0) {
      setHoveredBin(null);
      return;
    }

    setHoveredBin(bin);
    console.info(`${bin?.id} | ${bin?.latitude.toFixed(4)}, ${bin?.longitude.toFixed(4)}`);

    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top;

    setTooltipPosition({ x: centerX, y: centerY });
  }, [isInteracting]);

  const handleMouseLeave = useCallback(() => {
    setHoveredBin(null);
  }, []);

  const clearHover = useCallback(() => {
    setHoveredBin(null);
  }, []);

  return {
    hoveredBin,
    setHoveredBin,
    tooltipPosition,
    handleMouseEnter,
    handleMouseLeave,
    clearHover,
  };
}

/**
 * Container component that manages hover state and renders both map and tooltip
 * @param {Object} props - Component props
 * @param {Array} props.bins - Array of bin data
 * @param {Object} props.binStyles - Styles for specific bins by ID
 * @param {Array} props.backgroundShapes - Array of background shapes to render
 * @param {Function} props.onBinClick - Callback when bin is clicked
 * @param {number} props.width - Container width
 * @param {number} props.height - Container height
 * @param {boolean} props.showFillLevel - Whether to show fill level indicators
 * @returns {JSX.Element} Container component with map and tooltip
 */
export default function InventoryMapContainer({
  bins = [],
  binStyles = {},
  backgroundShapes = [],
  onBinClick,
  width = 800,
  height = 600,
  showFillLevel = false,
}) {
  const styles = useStyles();

  // Track if user is currently interacting with the map (panning/zooming)
  const [isInteracting, setIsInteracting] = useState(false);

  const { hoveredBin, tooltipPosition, handleMouseEnter, handleMouseLeave, clearHover } = useHoverState(isInteracting);

  return (
    <div className={styles.container}>
      <InventoryMap
        bins={bins}
        binStyles={binStyles}
        backgroundShapes={backgroundShapes}
        onBinClick={onBinClick}
        width={width}
        height={height}
        showFillLevel={showFillLevel}
        handleMouseEnter={handleMouseEnter}
        handleMouseLeave={handleMouseLeave}
        setIsInteracting={setIsInteracting}
        clearHover={clearHover}
      />

      <InventoryMapTooltip
        hoveredBin={hoveredBin}
        tooltipPosition={tooltipPosition}
      />
    </div>
  );
}
